import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { fetchGoogleReviews } from "../lib/api";

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
  </svg>
);

const Stars = ({ n }) => (
  <span className="inline-flex" aria-label={`${n} av 5`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} className={`h-4 w-4 ${i < Math.round(n || 0) ? "fill-[#FBBC05] text-[#FBBC05]" : "text-slate-300"}`} />
    ))}
  </span>
);

export default function GoogleReviews() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchGoogleReviews()
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ configured: false, reviews: [] }); });
    return () => { cancelled = true; };
  }, []);

  // Render nothing until real Google reviews are configured & available.
  if (!data || !data.configured || !data.reviews || data.reviews.length === 0) return null;

  return (
    <section id="google-omdomen" className="bg-white py-16 lg:py-24" data-testid="google-reviews-section">
      <div className="mx-auto max-w-7xl px-6">
        <div className="reveal text-center max-w-2xl mx-auto">
          <p className="flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-[#1B45C4]">
            <GoogleG /> Google-omdömen
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-slate-900">Vad våra kunder säger</h2>
          <div className="mt-4 flex items-center justify-center gap-3" data-testid="google-reviews-summary">
            <span className="text-3xl font-extrabold text-slate-900">{(data.rating || 0).toFixed(1)}</span>
            <Stars n={data.rating} />
            <span className="text-sm text-slate-500">({data.userRatingCount} recensioner)</span>
          </div>
        </div>

        <div className="reveal mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {data.reviews.slice(0, 6).map((r, i) => (
            <article key={`${r.authorName}-${i}`} data-testid={`google-review-${i}`}
              className="flex flex-col rounded-3xl bg-[#f8fafc] ring-1 ring-slate-100 p-6 shadow-sm">
              <div className="flex items-center gap-3">
                {r.authorPhotoUri ? (
                  <img src={r.authorPhotoUri} alt="" className="h-10 w-10 rounded-full object-cover" loading="lazy" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1B45C4] text-white font-bold">
                    {(r.authorName || "?").charAt(0)}
                  </div>
                )}
                <div className="min-w-0">
                  {r.authorUri ? (
                    <a href={r.authorUri} target="_blank" rel="noreferrer" className="block truncate font-bold text-slate-900 hover:underline">{r.authorName}</a>
                  ) : (
                    <span className="block truncate font-bold text-slate-900">{r.authorName}</span>
                  )}
                  <div className="flex items-center gap-2">
                    <Stars n={r.rating} />
                    {r.relativeTime && <span className="text-xs text-slate-400">{r.relativeTime}</span>}
                  </div>
                </div>
                <span className="ml-auto shrink-0"><GoogleG /></span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-600 line-clamp-6">{r.text}</p>
            </article>
          ))}
        </div>

        {data.googleMapsUri && (
          <div className="reveal mt-8 text-center">
            <a href={data.googleMapsUri} target="_blank" rel="noreferrer" data-testid="google-reviews-maps-link"
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 hover:ring-[#1B45C4]/40 hover:text-[#1B45C4] transition-colors">
              <GoogleG /> Se alla recensioner på Google
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
